"use client"

import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { ChevronLeft, Compass, MessageCircle, ShoppingBag, type LucideIcon } from "lucide-react"
import { useUserRole } from "@/providers/user-role-provider"
import { readClientView } from "@/lib/client-view"
import { stripLocalePrefix, isGlassNavPath, isChromelessPath } from "@/i18n/routing"
import { isRootPath, parentPath } from "@/lib/nav-back"
import { isNativeApp, isWebOnlyPath } from "@/lib/native"
import { useEffect, useRef, useState } from "react"

// The top-left of the signed-in chrome. Mirrors the bell top-right on every
// breakpoint, with the same visibility rules, so the strip reads as
// [action] — wordmark — bell.
//
// What it is depends on where you are:
//   - Off the dashboard: Dashboard (home for the active mode).
//   - On the dashboard: the one thing that mode reaches for most —
//     Barber → Rewards, Owner → Messages, Client → Explore shops.
//   - Already on that page: Dashboard again, so it is never a link to itself.
//
// BELOW A ROOT IT IS BACK INSTEAD. The shell has no navigation bar, so this was
// the only control in the corner where a back button belongs — and it was a
// shortcut: from a shop page it read "Dashboard" and threw you to the
// dashboard, with no way back to the listings you were browsing. See
// lib/nav-back for which screens count as roots and where Back goes when
// there is no history to pop (a push notification can open a shop page cold).
const HIDE_PREFIXES: ReadonlyArray<string> = ["/auth", "/clients", "/preview"]

function shouldHide(rawPathname: string | null): boolean {
  if (!rawPathname) return false
  const pathname = stripLocalePrefix(rawPathname)
  if (isChromelessPath(pathname)) return true
  if (isGlassNavPath(pathname)) return true
  return HIDE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

export function TopLeftAction() {
  const pathname = usePathname()
  const router = useRouter()
  const { userId, role, actualUserType, isLoading } = useUserRole()
  // How many times this web view has navigated since it loaded. router.back()
  // is only safe while this is above zero; before that the entry behind us
  // belongs to the shell's launch, not to anything the person did.
  const navigationsRef = useRef(0)
  const firstPathRef = useRef<string | null>(null)
  useEffect(() => {
    if (firstPathRef.current === null) firstPathRef.current = pathname ?? ""
    else if (pathname !== firstPathRef.current) navigationsRef.current += 1
  }, [pathname])
  const [clientView, setClientView] = useState(false)
  // Effect, not render: isNativeApp() is false in SSR and true in the shell.
  const [native, setNative] = useState(false)
  useEffect(() => setNative(isNativeApp()), [])
  useEffect(() => {
    setClientView(readClientView() || (pathname ?? "").startsWith("/dashboard/client"))
  }, [pathname])

  if (isLoading || !userId) return null
  if (shouldHide(pathname)) return null

  const effective = role ?? actualUserType
  const mode: "barber" | "owner" | "client" = clientView ? "client" : effective === "owner" ? "owner" : "barber"
  const onDashboard = (pathname ?? "").startsWith("/dashboard")

  const home = mode === "client" ? "/dashboard/client" : mode === "owner" ? "/dashboard/owner" : "/dashboard/barber"
  const reach =
    mode === "client"
      ? { label: "Explore", href: "/listings", icon: Compass }
      : mode === "owner"
        ? { label: "Messages", href: "/dashboard/owner/messages", icon: MessageCircle }
        : { label: "Rewards", href: "/dashboard/barber/rewards", icon: ShoppingBag }

  // Rewards is not in the native app (lib/native.ts), so a barber there gets
  // no shortcut on the dashboard — Dashboard would only link to itself.
  const reachHidden = native && isWebOnlyPath(reach.href)

  const action: { label: string; href: string; icon: LucideIcon } | null =
    // Going back to the dashboard reads as a way back, so it carries the Back
    // caret, not the grid icon (founder, 2026-10-30).
    onDashboard && pathname !== reach.href && !reachHidden
      ? reach
      : pathname === home
        ? null
        : { label: "Dashboard", href: home, icon: ChevronLeft }

  const showBack = !isRootPath(pathname)
  const fallback = parentPath(pathname)

  // Plain text, no button: "‹ Back" (or the icon and the page's name) in the
  // ink colour, the same as the three-panel Back (the founder's call on the
  // 2026-10-28 review, after trying a white pill). A soft halo in the page
  // colour (top-left-key in globals.css) keeps it readable over a shop's
  // photo. Each state uses the icon the floating nav gives the same page.
  const pill =
    "top-left-key inline-flex h-8 items-center gap-1 rounded-full px-1 text-[13.5px] leading-none font-medium text-foreground border-0 transition-opacity duration-200 hover:opacity-70"
  const disc = "top-left-key-icon grid place-items-center"

  if (showBack && fallback) {
    return (
      <div className="fixed safe-top-action left-2 md:left-3 lg:left-[1.125rem] z-40">
        <button
          type="button"
          onClick={() => {
            // Pop only what this session actually pushed. Going "back" out of
            // the first page the app opened would leave the web view on
            // whatever preceded it, which is the sign-in route the shell
            // launches on.
            if (navigationsRef.current > 0) router.back()
            else router.push(fallback)
          }}
          aria-label="Back"
          className={pill}
        >
          <span className={disc} aria-hidden>
            <ChevronLeft strokeWidth={2} />
          </span>
          Back
        </button>
      </div>
    )
  }

  // The plain text control (see above) at every breakpoint and in both themes:
  // one control, one look, wherever the chrome sits.
  //
  // On the phone it is a compact 28px pill (h-7 at top-2.5) with tight
  // padding, so it reads as a quiet control next to the bell rather than a
  // slab: its centre sits on the same 24px line as the bell (36px at
  // top-1.5) and the wordmark. The Request-button size is desktop-only.
  //
  if (!action) return null

  return (
    <div className="fixed safe-top-action left-2 md:left-3 lg:left-[1.125rem] z-40">
      <Link href={action.href} className={pill}>
        <span className={disc} aria-hidden>
          <action.icon strokeWidth={2} />
        </span>
        {action.label}
      </Link>
    </div>
  )
}
